import { useState } from "react";
import { moveItem } from "./moveItem";

type IdState = {
  ids: string[];
  /** Next free suffix, so a re-added row never reuses a departed row's id. */
  seed: number;
};

const fresh = (count: number, seed: number): IdState => ({
  ids: Array.from({ length: count }, (_, i) => `item-${seed + i}`),
  seed: seed + count,
});

export type SortableIds = {
  /** One id per item, in the items' current order. */
  ids: string[];
  /** Mirrors appending an item. */
  append: () => void;
  /** Mirrors removing the item at `index`. */
  remove: (index: number) => void;
  /** Mirrors moving the item at `from` to `to`. */
  move: (from: number, to: number) => void;
};

/**
 * Stable ids for a list of plain values that carry no identity of their own.
 *
 * Both lists this serves — a rule's actions and a condition group's children —
 * are replaced wholesale on every keystroke, so object identity can't key a row
 * and the array index would re-key every row below a reorder, remounting its
 * pickers just as the drag lands. These ids travel with the item instead.
 *
 * The caller mirrors each structural change here and on its own array, so the
 * two stay in step. A length that no longer matches means the list was replaced
 * from outside (a form finished loading its rule, say) and the ids are
 * regenerated — nothing was dragged, so nothing is lost.
 */
export const useSortableIds = (count: number): SortableIds => {
  const [state, setState] = useState<IdState>(() => fresh(count, 0));

  const current = state.ids.length === count ? state : fresh(count, state.seed);
  if (current !== state) setState(current);

  return {
    ids: current.ids,
    append: () =>
      setState((s) => ({
        ids: [...s.ids, `item-${s.seed}`],
        seed: s.seed + 1,
      })),
    remove: (index) =>
      setState((s) => ({ ...s, ids: s.ids.filter((_, i) => i !== index) })),
    move: (from, to) =>
      setState((s) => ({ ...s, ids: moveItem(s.ids, from, to) })),
  };
};
